import React from 'react';
import type { AdminEventListItem } from '../redux/events/events.api';

interface Props {
  event: AdminEventListItem;
  onClick?: (event: AdminEventListItem) => void;
}

const formatStartDate = (e: AdminEventListItem): string => {
  const d = new Date(e.startDate);
  if (Number.isNaN(d.getTime())) return '—';
  const dateStr = d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  if (e.startTime) return `${dateStr} · ${e.startTime}`;
  return dateStr;
};

const initial = (firstName?: string, lastName?: string): string => {
  const source = firstName || lastName || '?';
  return source.charAt(0).toUpperCase();
};

interface MiniAvatarProps {
  avatarUrl?: string | null;
  firstName?: string;
  lastName?: string;
  size?: number;
}

const MiniAvatar = ({
  avatarUrl,
  firstName,
  lastName,
  size = 35,
}: MiniAvatarProps): React.JSX.Element => {
  const style = { width: size, height: size, fontSize: size * 0.4 };
  if (avatarUrl) {
    return (
      <img
        className="event-card__avatar"
        src={avatarUrl}
        alt=""
        style={style}
      />
    );
  }
  return (
    <div className="event-card__avatar event-card__avatar--blank" style={style}>
      {initial(firstName, lastName)}
    </div>
  );
};

export const EventCard = ({ event, onClick }: Props): React.JSX.Element => {
  const isStarted = event.status === 'IN_PROGRESS';
  const isClubOnly = event.onlyForClubMembers && event.clubId;

  const remainingPlaces =
    event.capacity?.max != null && event.freeSlotsLeft != null
      ? event.freeSlotsLeft
      : null;
  const hasFreeSlotsLeft = event.freeSlotsLeft != null;
  const isEventFull =
    remainingPlaces !== null && remainingPlaces <= 0;
  const showLimitedRibbon =
    hasFreeSlotsLeft &&
    event.freeSlotsLeft != null &&
    event.freeSlotsLeft > 0 &&
    event.freeSlotsLeft <= 5;
  const showRibbon = isEventFull || showLimitedRibbon;
  const ribbonText = isEventFull
    ? 'Full'
    : event.freeSlotsLeft === 1
    ? '1 place left'
    : `${event.freeSlotsLeft} places left`;

  const authorName = event.author
    ? `${event.author.firstName} ${event.author.lastName}`.trim()
    : '';

  const handleClick = (): void => onClick?.(event);

  return (
    <button
      type="button"
      className={
        isStarted ? 'event-card event-card--active' : 'event-card'
      }
      onClick={handleClick}
    >
      {event.imageUrl ? (
        <img
          className="event-card__image"
          src={event.imageUrl}
          alt=""
          loading="lazy"
        />
      ) : (
        <div className="event-card__image event-card__image--blank" />
      )}

      <div className="event-card__overlay event-card__overlay--top" />
      <div className="event-card__overlay event-card__overlay--bottom" />

      {showRibbon && (
        <div className="event-card__ribbon">
          <span className="event-card__ribbon-text">{ribbonText}</span>
        </div>
      )}

      <div className="event-card__top">
        <MiniAvatar
          avatarUrl={event.club?.imageUrl ?? event.author?.avatarUrl}
          firstName={event.author?.firstName}
          lastName={event.author?.lastName}
        />
        {event.club && (
          <>
            <span className="event-card__author">{event.club.name}</span>
            <span className="event-card__dot" />
          </>
        )}
        {event.author && (
          <span className="event-card__author">{authorName}</span>
        )}
      </div>

      <div className="event-card__content">
        <h3 className="event-card__title">{event.name}</h3>
        <div className="event-card__meta">
          <span>{formatStartDate(event)}</span>
          <span className="event-card__dot event-card__dot--meta" />
          <span>{event.location?.city || '—'}</span>
        </div>
        {isClubOnly && (
          <div className="event-card__club-badge">Club members only</div>
        )}
        <div className="event-card__footer">
          <span className="event-card__view-btn">
            {isEventFull ? 'RSVP' : 'View'}
          </span>
          {event.firstThreeParticipants &&
            event.firstThreeParticipants.length > 0 && (
              <div className="event-card__first-three">
                {event.firstThreeParticipants.slice(0, 3).map((p) => (
                  <MiniAvatar
                    key={p.id}
                    avatarUrl={p.avatarUrl}
                    firstName={p.firstName}
                    lastName={p.lastName}
                    size={25}
                  />
                ))}
              </div>
            )}
        </div>
      </div>
    </button>
  );
};
